<template>
  <el-drawer
    title="模型详情"
    :visible.sync="innerVisible"
    size="760px"
    @open="loadObjects"
    @closed="reset"
  >
    <div v-if="model" class="detail-body">
      <div class="detail-title">{{ model.name || model.file_path }}</div>

      <el-descriptions title="基本信息" :column="2" size="small" border>
        <el-descriptions-item label="模型 ID">
          <span class="mono">{{ model.id }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="模型类型">{{ dash(model.model_type) }}</el-descriptions-item>
        <el-descriptions-item label="权重类型">
          <template v-if="!editingType">
            <el-tag :type="weightTypeMeta(model.weight_type).tag" size="mini">{{ weightTypeMeta(model.weight_type).label }}</el-tag>
            <el-button type="text" size="mini" class="type-edit-btn" @click="startEditType">改</el-button>
          </template>
          <template v-else>
            <el-select v-model="draftType" size="mini" style="width:110px">
              <el-option v-for="(meta, key) in WEIGHT_TYPES" :key="key" :label="meta.label" :value="key" />
            </el-select>
            <el-button type="text" size="mini" :loading="savingType" @click="saveWeightType">保存</el-button>
            <el-button type="text" size="mini" :disabled="savingType" @click="editingType = false">取消</el-button>
          </template>
        </el-descriptions-item>
        <el-descriptions-item label="来源">
          <el-tag :type="sourceMeta(model.source).tag" size="mini">{{ sourceMeta(model.source).label }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="版本 / 阶段">
          {{ dash(model.version) }} · {{ stageMeta(model.stage).label }}
        </el-descriptions-item>
        <el-descriptions-item label="实例引用">
          <el-tag v-if="model.in_use" type="success" size="mini">有实例在用</el-tag>
          <span v-else class="muted">未被引用</span>
        </el-descriptions-item>
        <el-descriptions-item label="上传人">{{ dash(model.uploaded_by) }}</el-descriptions-item>
        <el-descriptions-item label="创建时间">{{ formatTime(model.created_at) }}</el-descriptions-item>
        <el-descriptions-item label="本地权重">
          <span class="mono">{{ model.file_path }}</span>
        </el-descriptions-item>
      </el-descriptions>

      <el-descriptions
        title="类别名"
        :column="1"
        size="small"
        border
        class="section-gap"
      >
        <el-descriptions-item label="类别顺序">
          <template v-if="classNames.length">
            <el-tag
              v-for="(name, index) in classNames"
              :key="index"
              size="mini"
              class="class-tag"
            >{{ index }} {{ name }}</el-tag>
          </template>
          <span v-else class="muted">无标注</span>
        </el-descriptions-item>
      </el-descriptions>

      <el-descriptions
        v-if="hasSummary"
        title="训练信息"
        :column="2"
        size="small"
        border
        class="section-gap"
      >
        <el-descriptions-item label="训练 run">
          <span class="mono">{{ dash(summary.run_name) }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="磁盘目录名">
          <span class="mono">{{ dash(summary.run_name_on_disk) }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="数据集" :span="2">
          <span class="mono">{{ dash(summary.dataset) }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="训练轮数">{{ formatEpochs(summary.epochs_done, summary.epochs) }}</el-descriptions-item>
        <el-descriptions-item label="batch / imgsz">
          {{ dash(summary.batch) }} / {{ dash(summary.imgsz) }}
        </el-descriptions-item>
        <el-descriptions-item label="训练设备">{{ dash(summary.device) }}</el-descriptions-item>
        <el-descriptions-item label="归档时间">{{ formatTime(summary.pushed_at) }}</el-descriptions-item>
        <el-descriptions-item label="训练开始">{{ formatTime(summary.train_started_at) }}</el-descriptions-item>
        <el-descriptions-item label="训练结束">{{ formatTime(summary.train_finished_at) }}</el-descriptions-item>
        <el-descriptions-item label="训练源路径" :span="2">
          <span class="mono">{{ dash(summary.source_path) }}</span>
        </el-descriptions-item>
      </el-descriptions>

      <el-alert
        v-else
        class="section-gap"
        title="该模型没有训练元数据"
        type="info"
        :closable="false"
        show-icon
      />

      <div class="section-gap">
        <div class="obj-header">
          <span class="section-title">MinIO 产物清单</span>
          <span v-if="objectPath" class="obj-count">共 {{ objects.length }} 个对象</span>
        </div>
        <div class="obj-path">
          <span v-if="objectPath" class="mono">{{ objectPath }}</span>
          <span v-else class="muted">该模型没有 MinIO 副本</span>
        </div>
        <el-table
          v-loading="loading"
          :data="objects"
          empty-text="没有读到对象"
          border
          size="mini"
          style="width:100%"
        >
          <el-table-column label="对象" min-width="300" show-overflow-tooltip>
            <template slot-scope="{ row }">
              <span class="mono">{{ row.name }}</span>
            </template>
          </el-table-column>
          <el-table-column label="大小" width="100" align="right">
            <template slot-scope="{ row }">{{ formatSize(row.size) }}</template>
          </el-table-column>
          <el-table-column label="修改时间" width="160">
            <template slot-scope="{ row }">{{ formatTime(row.mtime) }}</template>
          </el-table-column>
        </el-table>
        <!-- 拉清单失败时把原因留在页面上 -->
        <div v-if="error" class="load-error">{{ error }}</div>
      </div>

      <div class="drawer-footer">
        <el-button size="small" @click="innerVisible = false">关闭</el-button>
      </div>
    </div>
  </el-drawer>
</template>

<script>
import dayjs from 'dayjs';
import { fetchModelObjects, updateModelWeightType } from '../api/blissrose/inference';
import { WEIGHT_TYPES } from '../utils/labels';

const STAGE_META = {
  staging: { label: '待上线', tag: 'info' },
  production: { label: '生产', tag: 'success' },
  archived: { label: '已归档', tag: 'warning' },
};

const SOURCE_META = {
  upload: { label: '上传', tag: 'info' },
  minio: { label: 'MinIO', tag: 'success' },
  deploy: { label: '部署', tag: 'warning' },
};

/** 模型详情抽屉：产物清单现拉一次；权重类型可就地改（改完发 updated 由父组件重拉） */
export default {
  name: 'ModelDetailDrawer',

  props: {
    visible: { type: Boolean, default: false },
    /** 模型记录，来自模型列表；为空时不渲染内容 */
    model: { type: Object, default: null },
  },

  data() {
    return {
      WEIGHT_TYPES,
      loading: false,
      objects: [],
      objectPath: '',
      error: '',
      // 改权重类型的小编辑态
      editingType: false,
      draftType: '',
      savingType: false,
    };
  },

  computed: {
    innerVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit('update:visible', val);
      },
    },

    summary() {
      return (this.model && this.model.metrics) || {};
    },

    hasSummary() {
      return Object.keys(this.summary).length > 0;
    },

    classNames() {
      return (this.model && this.model.class_names) || [];
    },
  },

  methods: {
    async loadObjects() {
      if (!this.model) return;
      this.loading = true;
      this.error = '';
      try {
        const data = (await fetchModelObjects(this.model.id)).data;
        this.objects = data.objects || [];
        this.objectPath = data.object_path || '';
      } catch (err) {
        this.error = err.message || '读取产物清单失败';
      } finally {
        this.loading = false;
      }
    },

    reset() {
      this.objects = [];
      this.objectPath = '';
      this.error = '';
      this.editingType = false;
      this.savingType = false;
    },

    // ====== 改权重类型 ======

    startEditType() {
      this.draftType = (this.model && this.model.weight_type) || 'base';
      this.editingType = true;
    },

    async saveWeightType() {
      this.savingType = true;
      try {
        await updateModelWeightType(this.model.id, this.draftType);
        this.$message.success('权重类型已修改');
        this.editingType = false;
        this.$emit('updated');
      } catch (err) {
        // 错误提示由响应拦截器统一弹出
      } finally {
        this.savingType = false;
      }
    },

    // ====== 展示辅助 ======

    stageMeta(stage) {
      return STAGE_META[stage] || STAGE_META.staging;
    },

    sourceMeta(source) {
      return SOURCE_META[source] || { label: source || '-', tag: 'info' };
    },

    /** 未知类型按原值显示 */
    weightTypeMeta(weightType) {
      return WEIGHT_TYPES[weightType] || { label: weightType || '-', tag: 'info' };
    },

    /** 0 与 false 都是有效值，只有 null / undefined / 空串按缺省显示 */
    dash(value) {
      return value === null || value === undefined || value === '' ? '-' : value;
    },

    /** 轮数显示成「实际 / 配置」 */
    formatEpochs(done, configured) {
      const actual = done === null || done === undefined ? '-' : done;
      return `${actual} / ${this.dash(configured)}`;
    },

    /** 小于 1 MB 的文件按 KB 显示 */
    formatSize(bytes) {
      if (bytes === null || bytes === undefined) return '-';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    },

    formatTime(value) {
      return value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-';
    },
  },
};
</script>

<style scoped>
.detail-body {
  padding: 0 20px 20px;
}

.detail-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 16px;
  word-break: break-all;
}

.section-gap {
  margin-top: 20px;
}

.obj-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.obj-count {
  font-size: 12px;
  color: #909399;
}

.obj-path {
  font-size: 12px;
  margin: 6px 0 10px;
  word-break: break-all;
}

.load-error {
  font-size: 12px;
  color: #f56c6c;
  line-height: 1.6;
  margin-top: 8px;
}

.class-tag {
  margin: 0 6px 4px 0;
}

.type-edit-btn {
  margin-left: 8px;
}

.mono {
  font-family: Consolas, Monaco, monospace;
}

.muted {
  color: #c0c4cc;
}

.drawer-footer {
  margin-top: 20px;
  text-align: right;
}
</style>
